import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Save, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import QuestionField, { isAnswerMissing } from "../components/shop/QuestionField";

export default function ShopQuestionnaire() {
  const { token } = useParams();
  const [data, setData] = useState(null); // null = loading, false = not found
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/api/shop/questionnaire/${token}`)
      .then(({ data }) => {
        if (!alive) return;
        setData(data);
        setAnswers(data.answers || {});
      })
      .catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const save = async (submit) => {
    setSaving(submit ? "submit" : "save");
    try {
      const { data: resp } = await api.put(`/api/shop/questionnaire/${token}`, { answers, submit });
      setAnswers(resp.answers || {});
      setData((d) => ({ ...d, service_status: resp.service_status, locked: resp.service_status !== "awaiting_questionnaire" && resp.service_status !== "submitted" }));
      if (submit) {
        toast.success("Thank you — your answers have been submitted.");
      } else {
        setSavedAt(new Date());
        toast.success("Progress saved — come back anytime to finish.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSaving(null);
    }
  };

  if (data === null) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="shop-questionnaire-loading">Loading…</div>;
  }
  if (data === false) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="shop-questionnaire-not-found">
        <XCircle className="w-12 h-12 mx-auto text-red-600" />
        <h1 className="mt-4 font-display text-2xl font-bold">This link is invalid or has expired.</h1>
        <p className="mt-2 text-ink-muted">Please contact hello@justcarecommunity.com if you need help.</p>
      </div>
    );
  }

  const missingRequired = (data.questionnaire || []).filter((q) => isAnswerMissing(q, answers[q.id]));

  if (data.locked) {
    return (
      <div className="container-jcc py-16 max-w-2xl" data-testid="page-shop-questionnaire">
        <h1 className="font-display text-3xl font-bold">{data.product_title}</h1>
        <div className="mt-8 rounded-2xl border border-teal/20 bg-teal/5 p-6 text-center" data-testid="shop-questionnaire-locked">
          <CheckCircle2 className="w-10 h-10 mx-auto text-teal" />
          <p className="mt-3 font-medium">Your questionnaire has been submitted and work is already underway.</p>
          <p className="mt-1 text-sm text-ink-muted">If you need to add or change anything, just reply to your confirmation email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-jcc py-16 max-w-2xl" data-testid="page-shop-questionnaire">
      <h1 className="font-display text-3xl font-bold">{data.product_title}</h1>
      <p className="mt-2 text-ink-muted">
        {data.service_status === "submitted"
          ? "You've already submitted this — you can still update it below before work begins."
          : "Please tell us a little more so we can get started. You can save your progress and come back later."}
      </p>

      <div className="mt-8 space-y-6">
        {(data.questionnaire || []).map((q) => (
          <div key={q.id}>
            <label className="block text-sm font-medium mb-1.5">{q.label}{q.required ? " *" : ""}</label>
            <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
          </div>
        ))}
        {(data.questionnaire || []).length === 0 && (
          <p className="text-ink-muted" data-testid="shop-questionnaire-empty">There's nothing to fill in for this order yet.</p>
        )}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button onClick={() => save(false)} disabled={!!saving} className="btn-outline justify-center disabled:opacity-60" data-testid="shop-questionnaire-save-btn">
          {saving === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save progress
        </button>
        <button onClick={() => save(true)} disabled={!!saving || missingRequired.length > 0}
          className="btn-teal justify-center disabled:opacity-60" data-testid="shop-questionnaire-submit-btn">
          {saving === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit
        </button>
      </div>
      {savedAt && (
        <p className="mt-3 text-xs text-ink-soft" data-testid="shop-questionnaire-saved-note">
          Saved at {savedAt.toLocaleTimeString()} — this link stays active until you submit.
        </p>
      )}
    </div>
  );
}
